// Pure status-code domain data, shared by backend and frontend. The frontend's
// Tailwind/color theme for these buckets lives separately in
// packages/frontend/src/features/wrkthru/bucket-theme.ts.

export type PriorityBucket = 'P1' | 'P2' | 'P3' | 'P4' | 'P5';

/**
 * Canonical Bravo / Media Trader Motivations status codes.
 * Strings match the headers in the motivations doc so the prioritizer can
 * round-trip them verbatim.
 */
export type WrkthruStatusCode =
  | 'CRITICAL_FAILURE'
  | 'FAILURE_TO_LAUNCH'
  | 'NOT_RUNNING'
  | 'CRITICAL_UNDERDELIVERY'
  | 'RUNAWAY_SPEND'
  | 'REVENUE_CAP_REACHED'
  | 'UNDERDELIVERY_RISK'
  | 'MARGIN_EROSION'
  | 'OVERDELIVERY_OVERSPEND'
  | 'APPROACHING_REVENUE_CAP'
  | 'OVERDELIVERY_AT_COST'
  | 'UNDERSPEND_UNDERDELIVERY'
  | 'SLOW_CAMPAIGN'
  | 'SLIGHT_UNDERDELIVERY'
  | 'MARGIN_OPPORTUNITY'
  | 'EFFICIENT_OVERDELIVERY'
  | 'ON_TRACK'
  | 'UNKNOWN';

export const STATUS_TO_BUCKET: Record<WrkthruStatusCode, PriorityBucket> = {
  CRITICAL_FAILURE: 'P1',
  FAILURE_TO_LAUNCH: 'P1',
  NOT_RUNNING: 'P1',
  CRITICAL_UNDERDELIVERY: 'P1',
  RUNAWAY_SPEND: 'P1',
  REVENUE_CAP_REACHED: 'P1',
  UNDERDELIVERY_RISK: 'P2',
  MARGIN_EROSION: 'P2',
  OVERDELIVERY_OVERSPEND: 'P2',
  APPROACHING_REVENUE_CAP: 'P2',
  OVERDELIVERY_AT_COST: 'P3',
  UNDERSPEND_UNDERDELIVERY: 'P3',
  SLOW_CAMPAIGN: 'P3',
  SLIGHT_UNDERDELIVERY: 'P3',
  MARGIN_OPPORTUNITY: 'P4',
  EFFICIENT_OVERDELIVERY: 'P4',
  ON_TRACK: 'P5',
  UNKNOWN: 'P5' // unknown is shown in the grey fill, not surfaced as a section card
};

export const STATUS_TAG_LABEL: Record<WrkthruStatusCode, string> = {
  CRITICAL_FAILURE: 'Critical Failure',
  FAILURE_TO_LAUNCH: 'Failure to Launch',
  NOT_RUNNING: 'Not Running',
  CRITICAL_UNDERDELIVERY: 'Critical Underdelivery',
  RUNAWAY_SPEND: 'Runaway Spend',
  REVENUE_CAP_REACHED: 'Revenue Cap Reached',
  UNDERDELIVERY_RISK: 'Underdelivery Risk',
  MARGIN_EROSION: 'Margin Erosion',
  OVERDELIVERY_OVERSPEND: 'Overdelivery + Overspend',
  APPROACHING_REVENUE_CAP: 'Approaching Revenue Cap',
  OVERDELIVERY_AT_COST: 'Overdelivery (At Cost)',
  UNDERSPEND_UNDERDELIVERY: 'Underspend + Underdelivery',
  SLOW_CAMPAIGN: 'Slow Campaign',
  SLIGHT_UNDERDELIVERY: 'Slight Underdelivery',
  MARGIN_OPPORTUNITY: 'Margin Opportunity',
  EFFICIENT_OVERDELIVERY: 'Efficient Overdelivery',
  ON_TRACK: 'On Track',
  UNKNOWN: 'Status Unknown'
};

/** Buckets in display order. */
export const BUCKET_ORDER: PriorityBucket[] = ['P1', 'P2', 'P3', 'P4', 'P5'];

/** Inverse of STATUS_TO_BUCKET (excludes UNKNOWN). */
export const BUCKET_TO_STATUSES: Record<PriorityBucket, WrkthruStatusCode[]> = BUCKET_ORDER.reduce(
  (acc, b) => {
    acc[b] = (Object.keys(STATUS_TO_BUCKET) as WrkthruStatusCode[]).filter(
      s => STATUS_TO_BUCKET[s] === b && s !== 'UNKNOWN'
    );
    return acc;
  },
  {} as Record<PriorityBucket, WrkthruStatusCode[]>
);
